import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Mail, MapPin, Globe, Calendar,
  ShieldCheck, FileText, Scale, Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// =====================================================================
// Informations reelles Mibeko / MolaCorp
// =====================================================================
const COMPANY = {
  name: 'MolaCorp',
  foundedYear: 'Avril 2022',
  address: '01 av. OUA, Basoko, Ngaliema. Concession Prockoki',
  city: 'Kinshasa',
  country: "Republique Democratique du Congo",
};

const CONTACT = {
  generalEmail: 'contact@mibekordc.com',
  supportEmail: 'support@mibekordc.com',
};

const LINKS = {
  appWebsite: 'https://mibekordc.com',
};

const LEGAL = {
  privacyUrl: '/privacy',
  termsUrl: '/terms',
  disclaimer:
    "Mibeko est un outil d'information juridique. Les contenus diffusés ne remplacent en aucun cas la consultation d'un avocat ou d'un professionnel du droit.",
};

const APP_INFO = {
  name: 'Mibeko',
  version: '3.5',
};

const AboutPage = () => {
  const [stats, setStats] = useState({ articles: 0, textes: 0, categories: 0 });

  useEffect(() => {
    fetch(`${API_URL}/api/categories`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) return;
        const doms = data.flatMap(c => c.domains || []);
        setStats({
          categories: data.length,
          textes: doms.length,
          articles: doms.reduce((s, d) => s + (d.total_articles || 0), 0),
        });
      })
      .catch(() => {});
  }, []);

  return (
    <div className="about-page" data-testid="about-page">
      <div className="about-header">
        <Link to="/accueil" className="back-btn" data-testid="about-back-btn">
          <ArrowLeft />
        </Link>
        <h1>A propos</h1>
      </div>

      {/* Hero — Mibeko */}
      <section className="about-hero">
        <div className="about-hero-badge">
          <Scale style={{ width: 28, height: 28 }} />
        </div>
        <h2>{APP_INFO.name}</h2>
        <p className="about-hero-tagline">
          Votre guide juridique mobile pour la RDC
        </p>
        <div className="about-stats">
          <div>
            <strong data-testid="about-stat-articles">{stats.articles}</strong>
            <span>articles</span>
          </div>
          <div>
            <strong data-testid="about-stat-textes">{stats.textes}</strong>
            <span>textes de loi</span>
          </div>
          <div>
            <strong data-testid="about-stat-categories">{stats.categories}</strong>
            <span>categories</span>
          </div>
        </div>
      </section>

      {/* Edite par */}
      <section className="about-section">
        <h3 className="about-section-title">
          <Building2 /> Edite par
        </h3>
        <div className="about-card">
          <div className="about-company-name">{COMPANY.name}</div>
          <div className="about-detail-row">
            <Calendar style={{ width: 14, height: 14 }} />
            <span>Fondee en {COMPANY.foundedYear}</span>
          </div>
          <div className="about-detail-row">
            <MapPin style={{ width: 14, height: 14 }} />
            <span>{COMPANY.address}, {COMPANY.city}, {COMPANY.country}</span>
          </div>
        </div>
      </section>

      {/* Avertissement juridique */}
      <section className="about-section">
        <h3 className="about-section-title">
          <Info /> Avertissement juridique
        </h3>
        <div className="about-disclaimer" data-testid="about-disclaimer">
          {LEGAL.disclaimer}
        </div>
      </section>

      {/* Contact */}
      <section className="about-section">
        <h3 className="about-section-title">
          <Mail /> Contact
        </h3>
        <div className="about-card">
          <a
            href={`mailto:${CONTACT.supportEmail}`}
            className="about-contact-row"
            data-testid="about-support-email"
          >
            <Mail />
            <div>
              <div className="about-contact-label">Support utilisateur</div>
              <div className="about-contact-value">{CONTACT.supportEmail}</div>
            </div>
          </a>
          <a
            href={`mailto:${CONTACT.generalEmail}`}
            className="about-contact-row"
            data-testid="about-general-email"
          >
            <Mail />
            <div>
              <div className="about-contact-label">Contact general</div>
              <div className="about-contact-value">{CONTACT.generalEmail}</div>
            </div>
          </a>
        </div>
      </section>

      {/* Presence en ligne */}
      <section className="about-section">
        <h3 className="about-section-title">
          <Globe /> Presence en ligne
        </h3>
        <div className="about-social">
          <a
            href={LINKS.appWebsite}
            target="_blank"
            rel="noreferrer"
            className="about-social-link"
            data-testid="about-website"
          >
            <Globe /> mibekordc.com
          </a>
        </div>
      </section>

      {/* Mentions legales */}
      <section className="about-section">
        <h3 className="about-section-title">
          <ShieldCheck /> Mentions legales
        </h3>
        <div className="about-card">
          <Link to={LEGAL.privacyUrl} className="about-legal-link" data-testid="about-privacy-link">
            <FileText />
            <div>
              <div className="about-contact-label">Politique de confidentialite</div>
              <div className="about-contact-value">Comment nous protegeons vos donnees</div>
            </div>
          </Link>
          <Link to={LEGAL.termsUrl} className="about-legal-link" data-testid="about-terms-link">
            <FileText />
            <div>
              <div className="about-contact-label">Conditions d&apos;utilisation</div>
              <div className="about-contact-value">Les regles d&apos;usage de Mibeko</div>
            </div>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="about-footer">
        <p>© {new Date().getFullYear()} {COMPANY.name}. Tous droits reserves.</p>
        <p className="about-footer-small">Mibeko v{APP_INFO.version}</p>
      </footer>
    </div>
  );
};

export default AboutPage;
